import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  Hidden,
  Grid,
  Container,
  CardContent,
  CardHeader,
  Card,
  Divider,
  Toolbar
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { connect } from 'react-redux';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import FleetIoService from '../services/FleetIoService';
import GlobalSearch from '../components/GlobalSearch';
import { formatErrorMessage } from '../utils/common';
import { hide, show } from '../store/actions/busyIndicatorActions';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
  fleetIoId: number,
  stateId: number
}

const VehicleFleetioIssues: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  fleetIoId,
  stateId
}) => {
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [issuesData, setIssesData] = useState<any>({});
  const [searchText, setSearchText] = useState('');
  const [filterByCol, setFilterByCol] = useState('all_col');
  const [filteredRecords, setFilteredRecords] = useState<any[]>([]);

  const columns = [
    {
      field: 'number',
      headerName: 'Issue Number'
    },
    {
      field: 'state',
      headerName: 'State'
    },
    {
      field: 'description',
      headerName: 'Description'
    },
    {
      field: 'vehicle_name',
      headerName: 'Vehicle'
    },
    {
      field: 'reported_by',
      headerName: 'Reported By'
    }
  ];

  const handleRowClick = (id: number) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getColValue = (record: any, col: string) => {
    switch (col) {
      case 'number':
        return record.number?.toString() || '';
      case 'state':
        return record.state || '';
      case 'description':
        return record.description || '';
      case 'vehicle_name':
        return record.vehicle?.name || '';
      case 'reported_by':
        return record.reported_by?.name || '';
      default:
        return '';
    }
  };

  const onSearchTextChange = (event: any) => {
    const text = event.target.value.toLowerCase();
    setSearchText(text);

    if (!text) {
      setFilteredRecords(issuesData?.records || []);
      return;
    }

    const filtered = (issuesData?.records || []).filter((record: any) => {
      // Filter only via selected column
      if (filterByCol !== 'all_col') {
        return getColValue(record, filterByCol).toLowerCase().includes(text);
      }

      // Filter over all searchable fields
      return (record.number?.toString() || '').toLowerCase().includes(text)
        || (record.state || '').toLowerCase().includes(text)
        || (record.description || '').toLowerCase().includes(text)
        || (record.vehicle?.name || '').toLowerCase().includes(text)
        || (record.reported_by?.name || '').toLowerCase().includes(text)
        || (record.labels?.map((l: any) => l.name).join(' ') || '').toLowerCase().includes(text)
        || (record.custom_fields?.service_centre || '').toLowerCase().includes(text);
    });

    setFilteredRecords(filtered);
  };

  const onFilterByColumnChange = (col: string) => {
    setFilterByCol(col);
    // Re-run search with new column filter
    onSearchTextChange({ target: { value: searchText } });
  };

  const refreshData = () => {
    showBusyIndicator();

    const payload = {
      actionId: 200,
      stateId,
      queryParams: {
        vehicleId: fleetIoId.toString()
      }
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp) => {
        setIssesData(resp);
        setFilteredRecords(resp?.records || []);
        hideBusyIndicator();
      }).catch((err: any) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  useEffect(() => {
    refreshData();
  }, []);

  return (
    <>
      {issuesData && (
        <Container maxWidth="xl">
          <Toolbar style={{ justifyContent: 'flex-end' }}>
            <GlobalSearch
              searchedText={searchText}
              onSearchTextChange={onSearchTextChange}
              showFilterByColumn
              columns={columns}
              filterByColumn={filterByCol}
              onFilterByColumnChange={onFilterByColumnChange}
              width="300px"
            />
          </Toolbar>

          <Hidden mdDown>
            {/* Desktop/Table Layout */}
            <TableContainer component={Paper} elevation={3} sx={{ mt: 2, borderRadius: 2 }}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell />
                    <TableCell><strong>Issue Number</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Created At</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Vehicle</strong></TableCell>
                    <TableCell><strong>Reported By</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record: any) => (
                      <React.Fragment key={record.id}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => handleRowClick(record.id)}
                            >
                              {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{record.number || 'N/A'}</TableCell>
                          <TableCell>{record.state || 'Unknown'}</TableCell>
                          <TableCell>{record.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}</TableCell>
                          <TableCell>{record.description || 'No description available'}</TableCell>
                          <TableCell>{record.vehicle?.name || 'No vehicle assigned'}</TableCell>
                          <TableCell>{record.reported_by?.name || 'Anonymous'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="h4" gutterBottom>Details of issue</Typography>
                                <Table size="small" aria-label="details">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                      <TableCell><strong>Labels</strong></TableCell>
                                      <TableCell><strong>Resolved At</strong></TableCell>
                                      <TableCell><strong>Due Date</strong></TableCell>
                                      <TableCell><strong>Service Centre</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell>{record.labels?.length ? record.labels.map((label: any) => label.name).join(', ') : 'No Labels'}</TableCell>
                                      <TableCell>{record.resolved_at ? new Date(record.resolved_at).toLocaleString() : 'Not Resolved'}</TableCell>
                                      <TableCell>{record.due_date ? new Date(record.due_date).toLocaleString() : 'No Due Date'}</TableCell>
                                      <TableCell>{record.custom_fields?.service_centre || 'N/A'}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>No issues found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>

          <Hidden mdUp>
            {/* Mobile/Responsive Layout */}
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record: any) => (
                <Card key={record.id} sx={{ mb: 2 }}>
                  <CardHeader
                    title={`Issue #${record.number || 'N/A'}`}
                    subheader={record.state || 'Unknown'}
                    action={(
                      <IconButton onClick={() => handleRowClick(record.id)}>
                        {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2">
                      <b>Created At:</b> {record.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Description:</b> {record.description || 'No description available'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Vehicle:</b> {record.vehicle?.name || 'No vehicle assigned'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Reported By:</b> {record.reported_by?.name || 'Anonymous'}
                    </Typography>
                    <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6">Details of Issue</Typography>
                        <Typography variant="body2"><b>Labels:</b> {record.labels?.length ? record.labels.map((label: any) => label.name).join(', ') : 'No Labels'}</Typography>
                        <Typography variant="body2"><b>Resolved At:</b> {record.resolved_at ? new Date(record.resolved_at).toLocaleString() : 'Not Resolved'}</Typography>
                        <Typography variant="body2"><b>Due Date:</b> {record.due_date ? new Date(record.due_date).toLocaleString() : 'No Due Date'}</Typography>
                        <Typography variant="body2"><b>Service Centre:</b> {record.custom_fields?.service_centre || 'N/A'}</Typography>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" sx={{ py: 2 }}>No issues found.</Typography>
            )}
          </Hidden>
        </Container>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
});

export default connect(null, mapDispatchToProps)(VehicleFleetioIssues);
